import React, { useState, useRef, useEffect, useCallback } from "react";
import { api } from "../api";

interface CCOption {
  cost_center_number: string;
  cost_center_name:   string;
}

interface SheetInfo {
  name: string;
  rowCount: number;
  type: string; // "plan" | "salary" | "bip" | "other"
}

interface ValidationMonths {
  apr: number; may: number; jun: number; jul: number; aug: number; sep: number;
  oct: number; nov: number; decm: number; jan: number; feb: number; mar: number;
  total: number;
}

interface SampleRow {
  account: string;
  code: string;
  months: Record<string, number>;
}

interface ReadInfo {
  sheet_name: string;
  data_start_row: number;
  month_start_col: number;
  month_end_col: number;
  detected_fy: string;
}

interface ValidationResult {
  valid: boolean;
  cost_center: { exists: boolean; number: string; name: string | null };
  accounts: { total: number; existing: number; new_count: number; new_accounts: { account: string; category: string; code: string }[] };
  skipped_calculated: number;
  has_existing_data?: boolean;
  existing_budget_rows?: number;
  existing_dimensions?: Record<string, number>;
  plan_sheet: string;
  matrix: { category: string; months: ValidationMonths }[];
  grand_totals: ValidationMonths;
  read_info?: ReadInfo;
  sample_rows?: SampleRow[];
}

interface Props { fiscalYear: string; onUploadSuccess?: () => void; }

const BudgetUploader: React.FC<Props> = ({ fiscalYear, onUploadSuccess }) => {
  const [file,        setFile]        = useState<File | null>(null);
  const [ccsByBU,     setCCsByBU]     = useState<Record<string, CCOption[]>>({});
  const [allBUs,      setAllBUs]      = useState<string[]>([]);
  const [selectedBU,  setSelectedBU]  = useState("");
  const [selectedCC,  setSelectedCC]  = useState("");
  const [dragging,    setDragging]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [result,      setResult]      = useState<{ ok: boolean; message: string; detail?: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sheet selection state
  const [sheets,         setSheets]         = useState<SheetInfo[]>([]);
  const [planSheet,      setPlanSheet]      = useState("");
  const [salarySheet,    setSalarySheet]    = useState("");
  const [bipSheet,       setBipSheet]       = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError,   setPreviewError]   = useState("");

  // Password state (for protected workbooks)
  const [needsPassword,  setNeedsPassword]  = useState(false);
  const [filePassword,   setFilePassword]   = useState("");

  // Validation state
  const [validation,       setValidation]       = useState<ValidationResult | null>(null);
  const [validating,       setValidating]       = useState(false);
  const [validationError,  setValidationError]  = useState("");
  const [showSampleRows,   setShowSampleRows]   = useState(true);

  useEffect(() => {
    api.get("/admin/cost-centers-by-bu", { params: { fy: fiscalYear } })
      .then(r => { setCCsByBU(r.data.by_bu || {}); setAllBUs(r.data.business_units || []); })
      .catch(() => {});
  }, []);

  const availableCCs: CCOption[] = selectedBU && ccsByBU[selectedBU]
    ? ccsByBU[selectedBU]
    : [];

  // Preview file to get sheet list
  const previewFile = useCallback(async (f: File, pw?: string) => {
    setPreviewLoading(true);
    setPreviewError("");
    setSheets([]);
    setPlanSheet("");
    setSalarySheet("");
    setBipSheet("");

    try {
      const formData = new FormData();
      formData.append("file", f);
      if (pw) formData.append("password", pw);

      const res = await api.post("/upload-budget/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const sheetList: SheetInfo[] = res.data.sheets;
      setSheets(sheetList);
      setNeedsPassword(false);

      // Auto-select based on detected types
      const yyStr = fiscalYear.replace(/^FY/i, "");
      const planMatch = sheetList.find(s => s.name.trim().toUpperCase() === `PLANFY${yyStr}`)
        || sheetList.find(s => s.type === "plan");
      const salMatch  = sheetList.find(s => s.type === "salary");
      const bipMatch  = sheetList.find(s => s.type === "bip");

      if (planMatch)  setPlanSheet(planMatch.name);
      if (salMatch)   setSalarySheet(salMatch.name);
      if (bipMatch)   setBipSheet(bipMatch.name);
    } catch (err: any) {
      if (err.response?.data?.needsPassword) {
        setNeedsPassword(true);
        setPreviewError("");
      } else {
        setPreviewError(err.response?.data?.error || "Failed to read workbook.");
      }
    } finally {
      setPreviewLoading(false);
    }
  }, [fiscalYear]);

  // Validate file against database
  const validateFile = useCallback(async () => {
    if (!file || !selectedCC || !planSheet) return;
    setValidating(true);
    setValidationError("");
    setValidation(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("costCenter", selectedCC);
      formData.append("fiscalYear", fiscalYear);
      formData.append("planSheet", planSheet);
      if (salarySheet) formData.append("salarySheet", salarySheet);
      if (filePassword) formData.append("password", filePassword);

      const res = await api.post("/upload-budget/validate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setValidation(res.data);
    } catch (err: any) {
      setValidationError(err.response?.data?.error || "Validation failed.");
    } finally {
      setValidating(false);
    }
  }, [file, selectedCC, planSheet, salarySheet, filePassword, fiscalYear]);

  const processFile = useCallback((f: File) => {
    setFile(f);
    setResult(null);
    setShowConfirm(false);
    setValidation(null);
    setValidationError("");
    previewFile(f);
  }, [previewFile]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) processFile(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f && (f.name.endsWith(".xlsx") || f.name.endsWith(".xls"))) processFile(f);
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    setShowConfirm(false);
    setSheets([]);
    setPlanSheet("");
    setSalarySheet("");
    setBipSheet("");
    setPreviewError("");
    setNeedsPassword(false);
    setFilePassword("");
    setValidation(null);
    setValidationError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleConfirmedUpload = async () => {
    if (!file || !selectedCC || !planSheet) return;
    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("costCenter", selectedCC);
      formData.append("fiscalYear", fiscalYear);
      formData.append("planSheet", planSheet);
      if (salarySheet) formData.append("salarySheet", salarySheet);
      if (bipSheet) formData.append("bipSheet", bipSheet);
      if (filePassword) formData.append("password", filePassword);

      const res = await api.post("/upload-budget", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { inserted } = res.data;
      const parts: string[] = [];
      if (inserted?.budgets)   parts.push(`${inserted.budgets} budget rows`);
      if (inserted?.employees) parts.push(`${inserted.employees} employees`);
      if (inserted?.headcount) parts.push(`${inserted.headcount} headcount entries`);
      if (inserted?.skippedCalculated) parts.push(`${inserted.skippedCalculated} computed accounts skipped`);

      setResult({
        ok: true,
        message: `Annual Budget ${fiscalYear} uploaded successfully.`,
        detail: parts.length > 0 ? parts.join(" · ") : undefined,
      });
      onUploadSuccess?.();
    } catch (err: any) {
      setResult({
        ok: false,
        message: err.response?.data?.error || "Upload failed. Check file and try again.",
      });
    } finally {
      setUploading(false);
      setShowConfirm(false);
    }
  };

  const ccLabel = availableCCs.find(c => c.cost_center_number === selectedCC);
  const sheetsReady = sheets.length > 0 && planSheet;
  const buSelected = !!selectedBU;

  // Type badge helper
  const typeBadge = (type: string) => {
    const colors: Record<string, { bg: string; fg: string; label: string }> = {
      plan:   { bg: "#dbeafe", fg: "#1d4ed8", label: "Plan" },
      salary: { bg: "#dcfce7", fg: "#15803d", label: "Salary" },
      bip:    { bg: "#fef9c3", fg: "#854d0e", label: "BIP" },
      other:  { bg: "#f1f5f9", fg: "#64748b", label: "Other" },
    };
    const c = colors[type] || colors.other;
    return (
      <span style={{
        display: "inline-block", padding: "2px 8px", borderRadius: 4,
        fontSize: 11, fontWeight: 600, background: c.bg, color: c.fg,
      }}>{c.label}</span>
    );
  };

  return (
    <div className="uploader">

      {/* ── BU filter ────────────────────────────────────────────────────── */}
      {allBUs.length > 0 && (
        <div className="uploader-field">
          <label className="uploader-field-label">Business Unit <span className="uploader-required">*</span></label>
          <select
            className="uploader-select"
            value={selectedBU}
            onChange={e => { setSelectedBU(e.target.value); setSelectedCC(""); }}
          >
            <option value="">Select a business unit...</option>
            {allBUs.map(bu => <option key={bu} value={bu}>{bu}</option>)}
          </select>
        </div>
      )}

      {/* ── Cost center selector ─────────────────────────────────────────── */}
      <div style={!buSelected ? { opacity: 0.45, pointerEvents: "none" } : undefined}>
      <div className="uploader-field">
        <label className="uploader-field-label">Cost Center <span className="uploader-required">*</span></label>
        <select
          className="uploader-select"
          value={selectedCC}
          onChange={e => setSelectedCC(e.target.value)}
          disabled={!buSelected}
        >
          <option value="">{buSelected ? "Select a cost center..." : "Select a business unit first"}</option>
          {availableCCs.map(cc => (
            <option key={cc.cost_center_number} value={cc.cost_center_number}>
              {cc.cost_center_number} — {cc.cost_center_name}
            </option>
          ))}
        </select>
      </div>

      {/* ── Section divider ─────────────────────────────────────────────── */}
      <div className="uploader-section-divider" />
      <div className="uploader-section-label">File Upload</div>

      {/* ── Drop zone ────────────────────────────────────────────────────── */}
      <div
        className={`uploader-dropzone${dragging ? " uploader-dropzone--over" : ""}${file ? " uploader-dropzone--has-file" : ""}`}
        onDragOver={e => { if (buSelected) { e.preventDefault(); setDragging(true); } }}
        onDragLeave={() => setDragging(false)}
        onDrop={buSelected ? handleDrop : (e) => e.preventDefault()}
        onClick={() => buSelected && !file && fileInputRef.current?.click()}
        aria-label="Upload file"
        role="button"
        tabIndex={0}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleFileInput}
        />
        {file ? (
          <div className="uploader-file-info">
            <span className="material-icons uploader-file-icon">description</span>
            <div>
              <div className="uploader-file-name">{file.name}</div>
              <div className="uploader-file-size">{(file.size / 1024).toFixed(0)} KB</div>
            </div>
            <button className="uploader-clear" onClick={e => { e.stopPropagation(); clearFile(); }}>
              <span className="material-icons">close</span>
            </button>
          </div>
        ) : (
          <div className="uploader-prompt">
            <span className="material-icons uploader-prompt-icon">table_chart</span>
            <span className="uploader-prompt-main">Drop budget .xlsx here or <span className="uploader-link">browse</span></span>
            <span className="uploader-prompt-sub">Annual budget template per cost center</span>
            <span className="uploader-format-hint">.xlsx files only</span>
          </div>
        )}
      </div>

      {/* ── Preview loading ──────────────────────────────────────────────── */}
      {previewLoading && (
        <div style={{ padding: "12px 0", color: "#6b7280", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <span className="material-icons" style={{ fontSize: 16, animation: "fv-spin 0.8s linear infinite" }}>sync</span>
          Reading workbook sheets...
        </div>
      )}

      {/* ── Preview error ────────────────────────────────────────────────── */}
      {previewError && (
        <div className="uploader-result uploader-result--err" style={{ marginTop: 8 }}>
          <span className="material-icons">error</span>
          <div><div className="uploader-result-msg">{previewError}</div></div>
        </div>
      )}

      {/* ── Password prompt (for protected workbooks) ────────────────────── */}
      {file && needsPassword && (
        <div style={{
          marginTop: 12, padding: 16, background: "#fffbeb",
          border: "1px solid #fde68a", borderRadius: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span className="material-icons" style={{ fontSize: 18, color: "#d97706" }}>lock</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#92400e" }}>
              Password Protected Workbook
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#78350f", marginBottom: 10 }}>
            This workbook is password-protected. Enter the password to read its contents.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="password"
              className="uploader-select"
              placeholder="Workbook password..."
              value={filePassword}
              onChange={e => setFilePassword(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && filePassword && file) {
                  previewFile(file, filePassword);
                }
              }}
              style={{ flex: 1, padding: "8px 12px", fontSize: 13 }}
            />
            <button
              className="uploader-submit"
              style={{ padding: "8px 16px", fontSize: 13 }}
              disabled={!filePassword || previewLoading}
              onClick={() => file && previewFile(file, filePassword)}
            >
              {previewLoading ? (
                <><span className="uploader-spinner" />Unlocking...</>
              ) : (
                <><span className="material-icons" style={{ fontSize: 16 }}>lock_open</span>Unlock</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Sheet selection ──────────────────────────────────────────────── */}
      {file && sheets.length > 0 && !result && (
        <div style={{
          marginTop: 12, padding: 16, background: "#f8fafc",
          border: "1px solid #e2e8f0", borderRadius: 8,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", marginBottom: 4 }}>
            Select Sheets to Import
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
            {sheets.length} sheet{sheets.length !== 1 ? "s" : ""} found. Choose which data to import.
          </div>

          {/* Available sheets list */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3af", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
              Sheets in Workbook
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sheets.map(s => (
                <div key={s.name} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "4px 10px", background: "#fff", border: "1px solid #e2e8f0",
                  borderRadius: 6, fontSize: 12, color: "#374151",
                }}>
                  {typeBadge(s.type)}
                  <span>{s.name}</span>
                  <span style={{ color: "#9ca3af", fontSize: 11 }}>{s.rowCount} rows</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sheet role dropdowns */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Plan sheet (required) */}
            <div className="uploader-field" style={{ marginBottom: 0 }}>
              <label className="uploader-field-label" style={{ marginBottom: 2 }}>
                Plan Sheet <span className="uploader-required">*</span>
              </label>
              <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>
                Expense budget data (TOTEXP rows with monthly amounts)
              </div>
              <select
                className="uploader-select"
                value={planSheet}
                onChange={e => setPlanSheet(e.target.value)}
              >
                <option value="">Select plan sheet...</option>
                {sheets.map(s => (
                  <option key={s.name} value={s.name}>{s.name} ({s.rowCount} rows)</option>
                ))}
              </select>
            </div>

            {/* Salary sheet (optional) */}
            <div className="uploader-field" style={{ marginBottom: 0 }}>
              <label className="uploader-field-label" style={{ marginBottom: 2 }}>
                Salary Sheet
                <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 6 }}>optional</span>
              </label>
              <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>
                Employee salary data (name, job title, annual salary). Imports into the Salary editor.
              </div>
              <select
                className="uploader-select"
                value={salarySheet}
                onChange={e => setSalarySheet(e.target.value)}
              >
                <option value="">None - skip salary import</option>
                {sheets.map(s => (
                  <option key={s.name} value={s.name}>{s.name} ({s.rowCount} rows)</option>
                ))}
              </select>
            </div>

            {/* BIP sheet (optional, only if salary is selected) */}
            {salarySheet && (
              <div className="uploader-field" style={{ marginBottom: 0 }}>
                <label className="uploader-field-label" style={{ marginBottom: 2 }}>
                  BIP Sheet
                  <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 6 }}>optional</span>
                </label>
                <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>
                  Employee BIP percentages. Matched to employees from the Salary sheet.
                </div>
                <select
                  className="uploader-select"
                  value={bipSheet}
                  onChange={e => setBipSheet(e.target.value)}
                >
                  <option value="">None - skip BIP import</option>
                  {sheets.map(s => (
                    <option key={s.name} value={s.name}>{s.name} ({s.rowCount} rows)</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Info about calculated accounts */}
          {salarySheet && (
            <div style={{
              marginTop: 12, padding: "8px 12px", background: "#eff6ff",
              border: "1px solid #bfdbfe", borderRadius: 6, fontSize: 12, color: "#1e40af",
              display: "flex", alignItems: "flex-start", gap: 8,
            }}>
              <span className="material-icons" style={{ fontSize: 16, marginTop: 1 }}>info</span>
              <span>
                Accounts 60010 (Salary), 60022 (BIP), 60100 (Insurance), 60120 (Payroll Tax), and 60130 (Retirement)
                will be skipped from the Plan sheet since they are computed from employee data.
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Validate button ──────────────────────────────────────────────── */}
      {file && sheetsReady && !result && !showConfirm && (
        <button
          className="uploader-submit"
          style={{ marginTop: 12, background: "#4f46e5" }}
          onClick={validateFile}
          disabled={!selectedCC || validating}
        >
          {validating ? (
            <><span className="uploader-spinner" />Validating...</>
          ) : (
            <><span className="material-icons">fact_check</span>Validate & Preview</>
          )}
        </button>
      )}

      {/* ── Validation error ───────────────────────────────────────────────── */}
      {validationError && (
        <div className="uploader-result uploader-result--err" style={{ marginTop: 8 }}>
          <span className="material-icons">error</span>
          <div><div className="uploader-result-msg">{validationError}</div></div>
        </div>
      )}

      {/* ── Validation modal ───────────────────────────────────────────────── */}
      {validation && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9000,
            background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}
          onClick={e => { if (e.target === e.currentTarget) setValidation(null); }}
        >
          <div style={{
            background: "#fff", borderRadius: 12, width: "100%", maxWidth: 960,
            maxHeight: "90vh", display: "flex", flexDirection: "column",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          }}>
            {/* Modal header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 20px", borderBottom: "1px solid #e2e8f0",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="material-icons" style={{ fontSize: 22, color: "#4f46e5" }}>fact_check</span>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Validation Results</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {validation.plan_sheet} · {validation.accounts.total} accounts
                  </div>
                </div>
              </div>
              <button
                onClick={() => setValidation(null)}
                style={{
                  background: "none", border: "none", cursor: "pointer", padding: 4,
                  borderRadius: 6, color: "#94a3b8",
                }}
              >
                <span className="material-icons" style={{ fontSize: 22 }}>close</span>
              </button>
            </div>

            {/* Modal body — scrollable */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

              {/* Status badges row */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                {/* Cost center */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
                  background: validation.cost_center.exists ? "#f0fdf4" : "#fef2f2",
                  border: `1px solid ${validation.cost_center.exists ? "#bbf7d0" : "#fecaca"}`,
                  borderRadius: 8, flex: 1, minWidth: 200,
                }}>
                  <span className="material-icons" style={{ fontSize: 18, color: validation.cost_center.exists ? "#16a34a" : "#dc2626" }}>
                    {validation.cost_center.exists ? "check_circle" : "cancel"}
                  </span>
                  <span style={{ fontSize: 13, color: "#1e293b" }}>
                    CC <strong>{validation.cost_center.number}</strong>
                    {validation.cost_center.exists
                      ? ` — ${validation.cost_center.name}`
                      : " — NOT FOUND"}
                  </span>
                </div>

                {/* Accounts summary */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
                  background: "#f0fdf4", border: "1px solid #bbf7d0",
                  borderRadius: 8, flex: 1, minWidth: 200,
                }}>
                  <span className="material-icons" style={{ fontSize: 18, color: "#16a34a" }}>check_circle</span>
                  <span style={{ fontSize: 13, color: "#1e293b" }}>
                    <strong>{validation.accounts.existing}</strong> existing ·{" "}
                    <strong>{validation.accounts.new_count}</strong> new
                    {validation.skipped_calculated > 0 && (
                      <> · <strong>{validation.skipped_calculated}</strong> skipped</>
                    )}
                  </span>
                </div>
              </div>

              {/* ── Overwrite warning ─────────────────────────────────────── */}
              {validation.has_existing_data && (
                <div style={{
                  marginBottom: 16, padding: "12px 16px", background: "#fef2f2",
                  border: "1px solid #fecaca", borderRadius: 8,
                  display: "flex", alignItems: "flex-start", gap: 10,
                }}>
                  <span className="material-icons" style={{ fontSize: 20, color: "#dc2626", marginTop: 1 }}>warning</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#991b1b", marginBottom: 4 }}>
                      Existing Data Will Be Overwritten
                    </div>
                    <div style={{ fontSize: 12, color: "#7f1d1d", lineHeight: 1.5 }}>
                      CC <strong>{validation.cost_center.number}</strong> already has{" "}
                      <strong>{validation.existing_budget_rows?.toLocaleString()}</strong> budget rows across{" "}
                      <strong>{Object.keys(validation.existing_dimensions || {}).length}</strong> dimensions
                      ({Object.keys(validation.existing_dimensions || {}).join(", ")}).
                      <br />
                      Uploading will <strong>erase all existing budget data</strong> for this cost center
                      (Annual Budget, July Forecast, October Forecast, and January Forecast)
                      and replace it with the data from this file.
                    </div>
                  </div>
                </div>
              )}

              {/* ── Data Preview — verify parser is reading correct columns ── */}
              {validation.sample_rows && validation.sample_rows.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <button
                    onClick={() => setShowSampleRows(!showSampleRows)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6, width: "100%",
                      background: "none", border: "none", cursor: "pointer", padding: "6px 0",
                      fontSize: 13, fontWeight: 600, color: "#1e293b",
                    }}
                  >
                    <span className="material-icons" style={{ fontSize: 16, transition: "transform 0.2s", transform: showSampleRows ? "rotate(0deg)" : "rotate(-90deg)" }}>expand_more</span>
                    <span className="material-icons" style={{ fontSize: 16, color: "#4f46e5" }}>preview</span>
                    Data Preview — Verify Correct Columns
                    {validation.read_info && (
                      <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 8, fontSize: 12 }}>
                        Sheet: {validation.read_info.sheet_name} · {validation.read_info.detected_fy} · Row {validation.read_info.data_start_row}+
                      </span>
                    )}
                  </button>
                  {showSampleRows && (
                    <>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8, paddingLeft: 22 }}>
                        First {validation.sample_rows.length} data rows as read by the parser. Confirm these values match your Excel sheet.
                      </div>
                      <div style={{ overflowX: "auto", border: "1px solid #ddd6fe", borderRadius: 8 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, whiteSpace: "nowrap" }}>
                          <thead>
                            <tr style={{ background: "#f5f3ff" }}>
                              <th style={{ textAlign: "left", padding: "7px 10px", fontWeight: 600, color: "#6d28d9", position: "sticky", left: 0, background: "#f5f3ff", minWidth: 200 }}>Account</th>
                              {["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"].map(m => (
                                <th key={m} style={{ textAlign: "right", padding: "7px 8px", fontWeight: 600, color: "#6d28d9", minWidth: 72 }}>{m}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {validation.sample_rows.map((row, idx) => (
                              <tr key={idx} style={{ borderTop: "1px solid #ede9fe", background: idx % 2 === 0 ? "#faf5ff" : "#fff" }}>
                                <td style={{
                                  padding: "5px 10px", fontWeight: 500, color: "#1e293b",
                                  position: "sticky", left: 0, background: idx % 2 === 0 ? "#faf5ff" : "#fff",
                                  maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis",
                                }} title={row.account}>
                                  {row.account}
                                </td>
                                {(["apr","may","jun","jul","aug","sep","oct","nov","decm","jan","feb","mar"] as string[]).map(m => (
                                  <td key={m} style={{
                                    textAlign: "right", padding: "5px 8px", fontFamily: "monospace",
                                    color: (row.months[m] || 0) < 0 ? "#dc2626" : "#374151",
                                  }}>
                                    {(row.months[m] || 0) !== 0
                                      ? `$${Math.round(row.months[m] || 0).toLocaleString()}`
                                      : "—"}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* New accounts table */}
              {validation.accounts.new_count > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: "#92400e", marginBottom: 8,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <span className="material-icons" style={{ fontSize: 16 }}>add_circle</span>
                    {validation.accounts.new_count} New Account{validation.accounts.new_count !== 1 ? "s" : ""} to Create
                  </div>
                  <div style={{
                    border: "1px solid #fde68a", borderRadius: 8, overflow: "hidden",
                    maxHeight: 200, overflowY: "auto",
                  }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: "#fffbeb" }}>
                          <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, color: "#78350f", width: 70 }}>Code</th>
                          <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, color: "#78350f" }}>Account</th>
                          <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, color: "#78350f", width: 140 }}>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validation.accounts.new_accounts.map((a, i) => (
                          <tr key={i} style={{ borderTop: "1px solid #fef3c7", background: i % 2 === 0 ? "#fffef5" : "#fff" }}>
                            <td style={{ padding: "5px 10px", fontFamily: "monospace", color: "#92400e" }}>{a.code || "—"}</td>
                            <td style={{ padding: "5px 10px", color: "#1e293b" }}>{a.account}</td>
                            <td style={{ padding: "5px 10px", color: "#6b7280" }}>{a.category}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Monthly totals by category */}
              <div style={{ marginBottom: 8 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 8,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span className="material-icons" style={{ fontSize: 16 }}>table_chart</span>
                  Monthly Totals by Category
                </div>
                <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, whiteSpace: "nowrap" }}>
                    <thead>
                      <tr style={{ background: "#f1f5f9" }}>
                        <th style={{ textAlign: "left", padding: "7px 10px", fontWeight: 600, color: "#475569", position: "sticky", left: 0, background: "#f1f5f9", minWidth: 170 }}>Category</th>
                        {["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar","Total"].map(m => (
                          <th key={m} style={{ textAlign: "right", padding: "7px 8px", fontWeight: 600, color: "#475569", minWidth: 72 }}>{m}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {validation.matrix.map((row, idx) => (
                        <tr key={idx} style={{ borderTop: "1px solid #e2e8f0" }}>
                          <td style={{ padding: "6px 10px", fontWeight: 500, color: "#1e293b", position: "sticky", left: 0, background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                            {row.category}
                          </td>
                          {(["apr","may","jun","jul","aug","sep","oct","nov","decm","jan","feb","mar","total"] as (keyof ValidationMonths)[]).map(m => (
                            <td key={m} style={{
                              textAlign: "right", padding: "6px 8px",
                              color: row.months[m] < 0 ? "#dc2626" : "#374151",
                              fontWeight: m === "total" ? 600 : 400,
                              fontFamily: "monospace",
                              background: idx % 2 === 0 ? "#fff" : "#f8fafc",
                            }}>
                              {row.months[m] !== 0
                                ? `$${Math.round(row.months[m]).toLocaleString()}`
                                : "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {/* Grand total row */}
                      <tr style={{ borderTop: "2px solid #94a3b8", background: "#f1f5f9" }}>
                        <td style={{ padding: "7px 10px", fontWeight: 700, color: "#0f172a", position: "sticky", left: 0, background: "#f1f5f9" }}>Grand Total</td>
                        {(["apr","may","jun","jul","aug","sep","oct","nov","decm","jan","feb","mar","total"] as (keyof ValidationMonths)[]).map(m => (
                          <td key={m} style={{
                            textAlign: "right", padding: "7px 8px", fontWeight: 700,
                            color: validation.grand_totals[m] < 0 ? "#dc2626" : "#0f172a",
                            fontFamily: "monospace",
                          }}>
                            ${Math.round(validation.grand_totals[m]).toLocaleString()}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div style={{
              display: "flex", gap: 10, justifyContent: "flex-end",
              padding: "14px 20px", borderTop: "1px solid #e2e8f0",
            }}>
              <button
                className="uploader-cancel"
                onClick={() => setValidation(null)}
              >
                Close
              </button>
              <button
                className="uploader-submit"
                disabled={!validation.cost_center.exists}
                onClick={() => { setValidation(null); setShowConfirm(true); }}
              >
                <span className="material-icons">cloud_upload</span>
                Proceed to Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm step / Upload button ─────────────────────────────────── */}
      {showConfirm ? (
        <div className="uploader-confirm">
          <div className="uploader-confirm-header">Confirm Import</div>
          <div className="uploader-confirm-body">
            {/* Overwrite warning in confirm dialog */}
            {validation?.has_existing_data && (
              <div style={{
                marginBottom: 12, padding: "10px 14px", background: "#fef2f2",
                border: "1px solid #fecaca", borderRadius: 8,
                display: "flex", alignItems: "center", gap: 8, fontSize: 13,
              }}>
                <span className="material-icons" style={{ fontSize: 18, color: "#dc2626" }}>warning</span>
                <span style={{ color: "#991b1b" }}>
                  This will <strong>erase and replace</strong> all existing budget data for CC {selectedCC} across all forecast dimensions.
                </span>
              </div>
            )}
            <div className="uploader-confirm-row">
              <span className="uploader-confirm-label">Financial Year</span>
              <span className="uploader-confirm-value">{fiscalYear}</span>
            </div>
            <div className="uploader-confirm-row">
              <span className="uploader-confirm-label">Dimension</span>
              <span className="uploader-confirm-value">Annual Budget {fiscalYear}</span>
            </div>
            <div className="uploader-confirm-row">
              <span className="uploader-confirm-label">Cost Center</span>
              <span className="uploader-confirm-value">
                {ccLabel ? `${selectedCC} — ${ccLabel.cost_center_name}` : selectedCC}
              </span>
            </div>
            <div className="uploader-confirm-row">
              <span className="uploader-confirm-label">File</span>
              <span className="uploader-confirm-value">{file?.name}</span>
            </div>
            <div className="uploader-confirm-row">
              <span className="uploader-confirm-label">Plan Sheet</span>
              <span className="uploader-confirm-value">{planSheet}</span>
            </div>
            {salarySheet && (
              <div className="uploader-confirm-row">
                <span className="uploader-confirm-label">Salary Sheet</span>
                <span className="uploader-confirm-value">{salarySheet}</span>
              </div>
            )}
            {bipSheet && (
              <div className="uploader-confirm-row">
                <span className="uploader-confirm-label">BIP Sheet</span>
                <span className="uploader-confirm-value">{bipSheet}</span>
              </div>
            )}
          </div>
          <div className="uploader-confirm-actions">
            <button className="uploader-cancel" onClick={() => setShowConfirm(false)}>
              Cancel
            </button>
            <button
              className={`uploader-submit uploader-confirm-ok${uploading ? " uploader-submit--loading" : ""}`}
              onClick={handleConfirmedUpload}
              disabled={uploading}
            >
              {uploading ? (
                <><span className="uploader-spinner" />Uploading...</>
              ) : (
                <><span className="material-icons">cloud_upload</span>Confirm Upload</>
              )}
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Result ───────────────────────────────────────────────────────── */}
      {result && (
        <div className={`uploader-result${result.ok ? " uploader-result--ok" : " uploader-result--err"}`}>
          <span className="material-icons">
            {result.ok ? "check_circle" : "error"}
          </span>
          <div>
            <div className="uploader-result-msg">{result.message}</div>
            {result.detail && <div className="uploader-result-detail">{result.detail}</div>}
          </div>
        </div>
      )}
      </div>{/* end grayed-out wrapper */}
    </div>
  );
};

export default BudgetUploader;
